import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Plus, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { LayerDeleteDialog } from '../components/LayerDeleteDialog'
import { LayerInsertDialog } from '../components/LayerInsertDialog'
import { useLayers, useDeleteLayer, useAddLayer, useInsertLayer, useUpdateLayer } from '../hooks'
import { createLayerColumns } from '../columns'
import { AdminRoute } from '@/components/auth/AdminRoute'
import type { Layer } from '@/types/entities'

export function LayerListPage() {
    const { t } = useTranslation()
    const [selectedLayer, setSelectedLayer] = useState<Layer | null>(null)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showInsertDialog, setShowInsertDialog] = useState(false)

    const { data: layersResponse, isLoading } = useLayers()
    const deleteLayer = useDeleteLayer()
    const addLayer = useAddLayer()
    const insertLayer = useInsertLayer()
    const updateLayer = useUpdateLayer()

    // Extract and memoize layers array to prevent unnecessary re-renders
    // layersResponse is now PaginatedResponse<Layer> (from select function)
    const layers: Layer[] = useMemo(() => {
        const dataArray = layersResponse?.data
        if (!dataArray || !Array.isArray(dataArray)) {
            return []
        }
        // Return the array - React Query's structural sharing should keep this stable
        return dataArray
    }, [layersResponse])

    // Keep latest layers reference for stable callbacks
    const layersRef = useRef(layers)
    useEffect(() => {
        layersRef.current = layers
    }, [layers])

    // Store mutation functions in refs to prevent callback recreation
    const insertLayerRef = useRef(insertLayer)
    useEffect(() => {
        insertLayerRef.current = insertLayer
    }, [insertLayer])

    const updateLayerRef = useRef(updateLayer)
    useEffect(() => {
        updateLayerRef.current = updateLayer
    }, [updateLayer])

    // Memoize callbacks to prevent re-creating on every render
    const handleDelete = useCallback((layer: Layer) => {
        setSelectedLayer(layer)
        setShowDeleteDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedLayer) {
            deleteLayer.mutate(selectedLayer.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedLayer(null)
                },
            })
        }
    }, [selectedLayer, deleteLayer])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export layers to CSV')
    }, [])

    const handleInsert = useCallback((layer: Layer) => {
        setSelectedLayer(layer)
        setShowInsertDialog(true)
    }, [])

    const handleInsertConfirm = useCallback(() => {
        if (selectedLayer) {
            const currentLayers = layersRef.current
            const currentIndex = currentLayers.findIndex((l) => l.id === selectedLayer.id)

            if (currentIndex === 0) {
                insertLayerRef.current.mutate({
                    insertAfterLayerID: selectedLayer.layerID,
                    description: '',
                })
            } else {
                const rowAbove = currentLayers[currentIndex - 1]
                insertLayerRef.current.mutate({
                    insertAfterLayerID: rowAbove.layerID,
                    description: '',
                })
            }
            setShowInsertDialog(false)
            setSelectedLayer(null)
        }
    }, [selectedLayer])

    const handleAddNew = useCallback(async () => {
        try {
            await addLayer.mutateAsync({ description: '' })
        } catch (error) {
            console.error('Add layer error:', error)
        }
    }, [addLayer])

    const handleUpdateDescription = useCallback((layer: Layer, newDescription: string) => {
        updateLayerRef.current.mutate({
            id: layer.id,
            data: { description: newDescription },
        })
    }, []) // No dependencies - uses ref

    const columns = useMemo(() => {
        return createLayerColumns(handleDelete, handleInsert, handleUpdateDescription, t)
    }, [handleDelete, handleInsert, handleUpdateDescription, t])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('layers.loadingLayers')}</div>
            </div>
        )
    }

    return (
        <AdminRoute>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('layers.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('layers.pageDescription')}
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <Button onClick={handleAddNew}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('layers.addNewLayer')}
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-lg border bg-card p-6 md:col-start-2">
                        <div className="flex items-center space-x-2">
                            <Layers className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('layers.totalLayers')}</p>
                                <p className="text-2xl font-bold">{layers.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="max-w-4xl mx-auto">
                    <DataTable
                        columns={columns}
                        data={layers}
                        searchKey="description"
                        searchPlaceholder={t('layers.searchPlaceholder')}
                        onExport={handleExport}
                        exportButtonText={t('layers.export')}
                        enableRowSelection={false}
                        disablePagination={true}
                        className="w-full"
                    />
                </div>

                {/* Dialogs */}
                <LayerDeleteDialog
                    open={showDeleteDialog}
                    onOpenChange={setShowDeleteDialog}
                    layer={selectedLayer}
                    onConfirm={handleDeleteConfirm}
                    isLoading={deleteLayer.isPending}
                />

                <LayerInsertDialog
                    open={showInsertDialog}
                    onOpenChange={setShowInsertDialog}
                    layer={selectedLayer}
                    onConfirm={handleInsertConfirm}
                    isLoading={insertLayer.isPending}
                />
            </div>
        </AdminRoute>
    )
}

